import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitPublicRating } from '../api/services/publicFormService';

interface Ratings {
  cleaning: number;
  internet: number;
  food: number;
}

const CATEGORIES: { key: keyof Ratings; label: string; emoji: string }[] = [
  { key: 'cleaning', label: 'Cleanliness', emoji: '🧹' },
  { key: 'internet', label: 'Internet', emoji: '📶' },
  { key: 'food', label: 'Food', emoji: '🍽️' },
];

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star
            className={`h-8 w-8 transition-colors ${
              star <= (hovered || value)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const PublicRatingForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('p') || '';

  const [roomNumber, setRoomNumber] = useState('');
  const [ratings, setRatings] = useState<Ratings>({ cleaning: 0, internet: 0, food: 0 });
  const [feedback, setFeedback] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!propertyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center text-gray-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-400" />
          <p className="text-lg font-medium">Invalid link</p>
          <p className="text-sm mt-1">Please use the link sent by your host.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-500">Your feedback helps us improve your stay.</p>
        </div>
      </div>
    );
  }

  const allRated = ratings.cleaning > 0 && ratings.internet > 0 && ratings.food > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomNumber.trim() || !allRated) return;

    setStatus('loading');
    setErrorMsg('');
    try {
      await submitPublicRating({
        property_id: propertyId,
        room_number: roomNumber.trim(),
        cleaning_rating: ratings.cleaning,
        internet_rating: ratings.internet,
        food_rating: ratings.food,
        feedback_text: feedback.trim() || undefined,
      });
      setStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-full mb-3">
            <Star className="h-7 w-7 text-yellow-500 fill-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Rate Your Stay</h1>
          <p className="text-gray-500 text-sm mt-1">Share your monthly feedback</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          {/* Room Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Room Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 101"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50"
              required
            />
          </div>

          {/* Star Ratings */}
          <div className="space-y-5">
            {CATEGORIES.map(({ key, label, emoji }) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{emoji} {label}</p>
                  {ratings[key] > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][ratings[key]]}
                    </p>
                  )}
                </div>
                <StarRating
                  value={ratings[key]}
                  onChange={(v) => setRatings((prev) => ({ ...prev, [key]: v }))}
                />
              </div>
            ))}
          </div>

          {/* Feedback */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Any comments? <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              placeholder="Your feedback helps improve the PG..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 resize-none"
            />
          </div>

          {/* Error */}
          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={status === 'loading' || !roomNumber || !allRated}
            className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Rating'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">Powered by AffordaNest</p>
      </div>
    </div>
  );
};

export default PublicRatingForm;
